/**
 * GameChat
 *
 * Reusable room chat using the same game socket (useGameSocket / gameSocketService).
 * Messages are stored in GameStateContext so chat persists across phases and is
 * shared. Loads history from the server on mount when roomCode is set.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useGameStateContext } from '../../context/useGameStateContext';
import useGameSocket from '../../hooks/useGameSocket';
import Send from '../../../assets/icons/Send.svg?react';
import type { ChatMessage } from '../../types/game.types';

const CHAT_HISTORY_LIMIT = 100;

function mapHistoryMessage(m: {
    message: string;
    username: string;
    playerId: string;
    playerLetter: string | null;
    timestamp: string;
}): ChatMessage {
    return {
        id: `${m.timestamp}-${m.playerId}`,
        username: m.username,
        content: m.message,
        timestamp: m.timestamp,
        playerLetter: m.playerLetter === 'A' || m.playerLetter === 'B' ? m.playerLetter : undefined,
        playerId: m.playerId,
    };
}

export const GameChat: React.FC = () => {
    const { chatMessages, setChatMessages, roomCode } = useGameStateContext();
    const socket = useGameSocket();
    const socketRef = useRef(socket);
    socketRef.current = socket;

    const [newMessage, setNewMessage] = useState('');
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    // Load chat history when room changes; merge with existing so we don't overwrite real-time messages
    useEffect(() => {
        if (!roomCode || !socketRef.current) return;

        socketRef.current.getChatHistory({ limit: CHAT_HISTORY_LIMIT }, (response) => {
            if (!response.success || !response.messages?.length) return;

            const fromHistory = response.messages.map(mapHistoryMessage);
            setChatMessages((prev: ChatMessage[]) => {
                const prevIds = new Set(prev.map((m: ChatMessage) => m.id));
                const newFromHistory = fromHistory.filter((m: ChatMessage) => !prevIds.has(m.id));
                if (newFromHistory.length === 0) return prev;
                const merged = [...prev, ...newFromHistory].sort((a, b) =>
                    (a.timestamp || '').localeCompare(b.timestamp || '')
                );
                return merged;
            });
        });
    }, [roomCode, setChatMessages]);

    // Scroll to bottom when new messages arrive
    useEffect(() => {
        scrollContainerRef.current?.scrollTo(0, scrollContainerRef.current.scrollHeight);
    }, [chatMessages]);

    const handleSubmit = (e: React.FormEvent): void => {
        e.preventDefault();
        if (!newMessage.trim() || !roomCode) return;

        socketRef.current.sendChatMessage(
            { message: newMessage.trim(), roomCode },
            (response) => {
                if (response.success) {
                    setNewMessage('');
                }
            }
        );
    };

    return (
        <div className="room_chat_container">
            <p className="section_title">Chat</p>
            <div className="room_chat_inside">
                <div
                    id="scrollable"
                    className="chat_scroll_container"
                    ref={scrollContainerRef}
                >
                    <div className="chat_scroll_inside">
                        {chatMessages.map((message) => (
                            <div key={message.id} className="message_container">
                                <p className="message">{`(${message.username}): ${message.content}`}</p>
                            </div>
                        ))}
                    </div>
                </div>
                <form className="new_chat_container" onSubmit={handleSubmit}>
                    <input
                        type="text"
                        value={newMessage}
                        placeholder="Message..."
                        onChange={(e) => setNewMessage(e.target.value)}
                        className="new_chat_input"
                    />
                    <Send
                        type="submit"
                        className="new_chat_submit"
                        onClick={handleSubmit}
                    />
                </form>
            </div>
        </div>
    );
};

export default GameChat;
